/**
 * SoCJourneyScatter
 *
 * Plots each completed EV charging session (excluding forced-mode) as a dot at
 * (startSoC, socGain) where socGain = endSoC − startSoC. The color of each dot
 * is a red→green gradient based on how much of the targeted SoC gain was
 * actually reached (endSoC / desiredSoC, clamped 0–1).
 *
 * Why this is useful:
 * - Reveals which start-SoC ranges tend to produce low SoC gains (dots
 *   clustered near y=0 indicate systemic under-charging at certain entry SoCs)
 * - Shows absolute SoC gain per session — a high start-SoC car getting +5%
 *   is clearly visible next to a low start-SoC car getting +60%
 * - The color gradient immediately shows target fulfilment without needing a
 *   separate outcome column: red = far from target, green = target met
 *
 * Built with pure SVG — no D3 dependency needed for a scatter plot of this scale.
 */

import { useMemo, useState, useRef, useCallback } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDown, Check } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { InfoTip } from '@/components/ui/info-tip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Slider } from '@/components/ui/slider'
import { Loader2, ScatterChart } from 'lucide-react'
import { useChargingSessions } from '@/hooks/use-monitoring'
import type { TimeRange } from '@/hooks/use-monitoring'
import { formatDate, formatPower } from '@/lib/utils'
import type { components } from '../../../../../types/flex-api'

type ChargingSession = components['schemas']['ChargingSession']

interface SoCJourneyScatterProps {
  poolID: string
  timeRange: TimeRange
  // ── Optional controlled props ─────────────────────────────────────────────
  /** Controlled success threshold (pp). When provided, internal state is bypassed. */
  threshold?: number
  onThresholdChange?: (v: number) => void
  /** Controlled early-unplug tolerance (minutes). When provided, internal state is bypassed. */
  earlyUnplugToleranceMin?: number
  onEarlyUnplugToleranceMinChange?: (v: number) => void
  /** Controlled hidden outcomes set. When provided, internal state is bypassed. */
  hiddenOutcomes?: Set<string>
  onHiddenOutcomesChange?: (s: Set<string>) => void
  /** Controlled hidden duration buckets set. When provided, internal state is bypassed. */
  hiddenDurations?: Set<string>
  onHiddenDurationsChange?: (s: Set<string>) => void
  /** Controlled hidden unplug timings set. When provided, internal state is bypassed. */
  hiddenTimings?: Set<string>
  onHiddenTimingsChange?: (s: Set<string>) => void
  /** Controlled hidden feasibilities set. When provided, internal state is bypassed. */
  hiddenFeasibilities?: Set<string>
  onHiddenFeasibilitiesChange?: (s: Set<string>) => void
  /**
   * Controlled selected session ID. When provided, the detail dialog for the
   * matching session is opened. Pass null / undefined to keep closed.
   */
  selectedSessionId?: string | null
  onSelectedSessionIdChange?: (id: string | null) => void
}

// ── Outcome categories (SoC target result) ─────────────────────────────────

type Outcome = 'success' | 'failed' | 'no_charge'

function getOutcome(session: ChargingSession, threshold: number): Outcome {
  if (session.unpluggedSoC === session.pluggedSoC) return 'no_charge'
  if (session.desiredSoC === undefined) return 'failed'
  const gap = session.unpluggedSoC - session.desiredSoC
  return gap >= -threshold ? 'success' : 'failed'
}

const OUTCOME_COLOR: Record<Outcome, string> = {
  success: '#10b981',
  failed: '#ef4444',
  no_charge: '#94a3b8',
}

const OUTCOME_LABEL: Record<Outcome, string> = {
  success: 'Met target',
  failed: 'Missed target',
  no_charge: 'No charge',
}

// ── Duration categories ────────────────────────────────────────────────────

type DurationBucket = 'tiny' | 'short' | 'medium' | 'long' | 'verylong'

function getDurationBucket(session: ChargingSession): DurationBucket {
  const ms = new Date(session.unpluggedAt).getTime() - new Date(session.pluggedAt).getTime()
  const hours = ms / (1000 * 60 * 60)
  if (hours < 1) return 'tiny'
  if (hours < 6) return 'short'
  if (hours < 12) return 'medium'
  if (hours <= 24) return 'long'
  return 'verylong'
}

const DURATION_COLOR: Record<DurationBucket, string> = {
  tiny: '#8b5cf6',
  short: '#06b6d4',
  medium: '#10b981',
  long: '#f97316',
  verylong: '#ef4444',
}

const DURATION_LABEL: Record<DurationBucket, string> = {
  tiny: '< 1 h',
  short: '1 – 6 h',
  medium: '6 – 12 h',
  long: '12 – 24 h',
  verylong: '> 24 h',
}

// ── Unplug-timing category ─────────────────────────────────────────────────

type UnplugTiming = 'early' | 'ontime'

function getUnplugTiming(session: ChargingSession, toleranceMin: number): UnplugTiming | null {
  if (!session.scheduledDepartureTime) return null
  const toleranceMs = toleranceMin * 60 * 1000
  return new Date(session.unpluggedAt) <
    new Date(new Date(session.scheduledDepartureTime).getTime() - toleranceMs)
    ? 'early'
    : 'ontime'
}

const UNPLUG_TIMING_COLOR: Record<UnplugTiming, string> = {
  early: '#ec4899',
  ontime: '#10b981',
}

const UNPLUG_TIMING_LABEL: Record<UnplugTiming, string> = {
  early: 'Early unplug',
  ontime: 'On time',
}

// ── Feasibility category ───────────────────────────────────────────────────
//
// A session is "infeasible" when the target SoC could not have been reached
// within the planned session duration even if the charger ran at max power the
// entire time. We derive the battery capacity from maxEnergy (Wh), compute the
// energy required to close the gap from pluggedSoC to desiredSoC, and compare
// it against (maxChargePower × plannedHours).
//
// Returns null when feasibility cannot be determined (no schedule, no target,
// or median power is 0 which indicates unreliable data).

type Feasibility = 'feasible' | 'infeasible' | 'unknown'

function getFeasibility(session: ChargingSession): Feasibility {
  if (
    !session.scheduledDepartureTime ||
    session.desiredSoC === undefined ||
    session.medianChargePower === 0
  )
    return 'unknown'
  const socGap = session.desiredSoC - session.pluggedSoC
  if (socGap <= 0) return 'feasible' // already at or above target when plugged in
  const plannedMs =
    new Date(session.scheduledDepartureTime).getTime() - new Date(session.pluggedAt).getTime()
  if (plannedMs <= 0) return 'infeasible'
  const plannedHours = plannedMs / (1000 * 60 * 60)
  const energyNeededWh = (socGap / 100) * session.maxEnergy
  const maxDeliverableWh = session.maxChargePower * plannedHours
  return energyNeededWh <= maxDeliverableWh ? 'feasible' : 'infeasible'
}

const FEASIBILITY_COLOR: Record<Feasibility, string> = {
  feasible: '#6366f1',
  infeasible: '#f59e0b',
  unknown: '#94a3b8',
}

const FEASIBILITY_LABEL: Record<Feasibility, string> = {
  feasible: 'Feasible',
  infeasible: 'Infeasible',
  unknown: 'Unknown',
}

// ── Target-fulfilment gradient color ──────────────────────────────────────
//
// Three-stop gradient anchored to shortfall in percentage points:
//   gap = 0         → green  (#10b981)  — target met or exceeded
//   gap = yellowAt  → yellow (#f59e0b)  — yellowAt pp short (= threshold slider)
//   gap ≥ yellowAt×2 → red  (#ef4444)  — saturates at double the yellow point
//
// When desiredSoC is undefined or zero, returns neutral grey.

function targetFulfilmentColor(
  endSoC: number,
  desiredSoC: number | undefined,
  yellowAt: number
): string {
  if (desiredSoC === undefined || desiredSoC <= 0) return '#94a3b8'
  const redAt = yellowAt * 2
  const gap = Math.min(redAt, Math.max(0, desiredSoC - endSoC))
  if (gap <= 0) return '#10b981' // green
  if (gap >= redAt) return '#ef4444' // red
  if (gap <= yellowAt) {
    // green (#10b981) → yellow (#f59e0b), t goes 0→1 as gap goes 0→yellowAt
    const t = gap / yellowAt
    const r = Math.round(16 + (245 - 16) * t)
    const g = Math.round(185 + (158 - 185) * t)
    const b = Math.round(129 + (11 - 129) * t)
    return `rgb(${r},${g},${b})`
  }
  // yellow (#f59e0b) → red (#ef4444), t goes 0→1 as gap goes yellowAt→redAt
  const t = (gap - yellowAt) / yellowAt
  const r = Math.round(245 + (239 - 245) * t)
  const g = Math.round(158 + (68 - 158) * t)
  const b = Math.round(11 + (68 - 11) * t)
  return `rgb(${r},${g},${b})`
}

const MARGIN = { top: 24, right: 24, bottom: 52, left: 52 }

interface TooltipData {
  x: number
  y: number
  session: ChargingSession
  outcome: Outcome
  pointColor: string
}

// ── Point shape ────────────────────────────────────────────────────────────

interface PlotPoint {
  session: ChargingSession
  outcome: Outcome
  durationBucket: DurationBucket
  unplugTiming: UnplugTiming | null
  feasibility: Feasibility
  durationHours: number
  socGain: number
  targetSoC: number | undefined
  pointColor: string
}

// ── FilterDropdown ──────────────────────────────────────────────────────────

interface FilterItem {
  key: string
  label: string
  color: string
  count: number
  checked: boolean
  onToggle: () => void
}

interface FilterDropdownProps {
  label: string
  hiddenCount: number
  items: FilterItem[]
  tooltip: React.ReactNode
  footer?: React.ReactNode
}

function FilterDropdown({ label, hiddenCount, items, tooltip, footer }: FilterDropdownProps) {
  const active = hiddenCount === 0
  return (
    <div className="flex items-center gap-1">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted focus:outline-none ${
              active ? 'border-border text-foreground' : 'border-primary text-primary'
            }`}
          >
            {label}
            {hiddenCount > 0 && (
              <span className="rounded-full bg-primary text-primary-foreground px-1.5 py-px text-[10px] font-semibold leading-none">
                {hiddenCount}
              </span>
            )}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="z-50 min-w-[200px] rounded-md border bg-popover text-popover-foreground shadow-md py-1"
            sideOffset={4}
            align="start"
          >
            {items.map((item) => (
              <DropdownMenu.CheckboxItem
                key={item.key}
                checked={item.checked}
                onCheckedChange={item.onToggle}
                onSelect={(e) => e.preventDefault()}
                className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-muted focus:outline-none select-none"
              >
                <DropdownMenu.ItemIndicator>
                  <Check className="h-3 w-3" />
                </DropdownMenu.ItemIndicator>
                {/* placeholder to keep alignment when unchecked */}
                {!item.checked && <span className="h-3 w-3" />}
                <span
                  className="h-2 w-2 rounded-full shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span style={{ color: item.color }}>{item.label}</span>
                <span className="ml-auto text-muted-foreground">{item.count}</span>
              </DropdownMenu.CheckboxItem>
            ))}
            {footer && (
              <>
                <DropdownMenu.Separator className="my-1 h-px bg-border" />
                <div className="px-3 py-2" onPointerDown={(e) => e.stopPropagation()}>
                  {footer}
                </div>
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <InfoTip content={tooltip} side="bottom" />
    </div>
  )
}

// ── Session detail visual ──────────────────────────────────────────────────
//
// Renders a timeline bar that visually shows the charging session:
//  - X axis: time from plug-in to max(unplug, scheduled departure)
//  - Filled area: SoC interpolated linearly between plug-in and unplug values
//  - Dashed line: max feasible SoC reachable at max power by departure time
//  - Vertical markers: plug-in, scheduled departure (+ tolerance band), unplug
//  - Stylised battery icon on the right annotated with capacity

interface SessionDetailVisualProps {
  session: ChargingSession
  outcome: Outcome
  earlyUnplugToleranceMin: number
  threshold: number
}

function SessionDetailVisual({
  session: s,
  outcome,
  earlyUnplugToleranceMin,
  threshold,
}: SessionDetailVisualProps) {
  const svgW = 1360
  const svgH = 540
  const marginTop = 60 // extra room for top tolerance bracket
  const marginBottom = 110 // x-axis labels (2 rows) + duration brackets
  const marginLeft = 56
  const marginRight = 140 // room for battery icon
  const chartW = svgW - marginLeft - marginRight
  const chartH = svgH - marginTop - marginBottom

  // ── Time domain ───────────────────────────────────────────────────────────
  const tPlug = new Date(s.pluggedAt).getTime()
  const tUnplug = new Date(s.unpluggedAt).getTime()
  const tDepart = s.scheduledDepartureTime ? new Date(s.scheduledDepartureTime).getTime() : null
  const tEnd = tDepart !== null ? Math.max(tUnplug, tDepart) : tUnplug
  // Add 4% padding on each side of the time domain
  const tSpan = tEnd - tPlug
  const tPad = tSpan * 0.04
  const tMin = tPlug - tPad
  const tMax = tEnd + tPad

  const xScale = (t: number) => ((t - tMin) / (tMax - tMin)) * chartW
  const yScale = (soc: number) => chartH - (soc / 100) * chartH // 0% at bottom, 100% at top

  // ── SoC fill polygon ──────────────────────────────────────────────────────
  const xPlug = xScale(tPlug)
  const xUnplug = xScale(tUnplug)
  const yPlugSoC = yScale(s.pluggedSoC)
  const yUnplugSoC = yScale(s.unpluggedSoC)
  const yBottom = chartH

  const fillPoints = [
    `${xPlug},${yBottom}`,
    `${xPlug},${yPlugSoC}`,
    `${xUnplug},${yUnplugSoC}`,
    `${xUnplug},${yBottom}`,
  ].join(' ')

  // ── Max feasible SoC line ─────────────────────────────────────────────────
  let maxFeasibleSoC: number | null = null
  if (tDepart !== null && s.maxChargePower > 0 && s.maxEnergy > 0 && s.medianChargePower !== 0) {
    const plannedHours = Math.max(0, tDepart - tPlug) / (1000 * 60 * 60)
    const addedSoC = ((s.maxChargePower * plannedHours) / s.maxEnergy) * 100
    maxFeasibleSoC = Math.min(100, s.pluggedSoC + addedSoC)
  }

  // ── Early unplug tolerance band ───────────────────────────────────────────
  const toleranceMs = earlyUnplugToleranceMin * 60 * 1000
  let toleranceBand: { x: number; w: number } | null = null
  if (tDepart !== null) {
    const tTolStart = tDepart - toleranceMs
    const x1 = xScale(Math.max(tTolStart, tMin))
    const x2 = xScale(Math.min(tDepart, tMax))
    if (x2 > x1) toleranceBand = { x: x1, w: x2 - x1 }
  }

  // ── Axis label formatting ─────────────────────────────────────────────────
  function fmtTime(ms: number) {
    const d = new Date(ms)
    return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  }
  function fmtDateShort(ms: number) {
    const d = new Date(ms)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  function fmtDuration(ms: number): string {
    const h = Math.floor(ms / (1000 * 60 * 60))
    const m = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))
    return h > 0 ? `${h}h ${m}m` : `${m}m`
  }

  const timing = getUnplugTiming(s, earlyUnplugToleranceMin)
  const feasibility = getFeasibility(s)
  const batteryKWh = s.maxEnergy / 1000

  // Duration values
  const actualDurationMs = tUnplug - tPlug
  const plannedDurationMs = tDepart !== null ? tDepart - tPlug : null

  // Avg power derived from energy transferred: ΔSoC × capacity / duration
  const avgPower: number | null = (() => {
    const durationH = actualDurationMs / (1000 * 60 * 60)
    if (durationH <= 0) return null
    const energyWh = ((s.unpluggedSoC - s.pluggedSoC) / 100) * s.maxEnergy
    if (energyWh <= 0) return null
    return energyWh / durationH // W
  })()

  // Colors
  const fillColor = OUTCOME_COLOR[outcome]
  const batteryColor = OUTCOME_COLOR[outcome]

  // Battery icon dimensions (right side)
  const batW = 60
  const batH = 110
  const batX = svgW - marginRight + 32
  const batY = marginTop + chartH / 2 - batH / 2
  const batFill = s.unpluggedSoC / 100

  // SoC label positions (avoid overlap)
  const labelsClose = Math.abs(yPlugSoC - yUnplugSoC) < 30

  // ── Duration bracket helpers ───────────────────────────────────────────────
  // Brackets are drawn below the chart in the bottom margin.
  // Row 0 (y = chartH + 15/30): x-axis time labels (time + date)
  // Row 1 (y = chartH + 62):    actual duration bracket
  // Row 2 (y = chartH + 88):    planned duration bracket (if present)
  const bracketY1 = chartH + 60 // actual duration bracket row
  const bracketY2 = chartH + 86 // planned duration bracket row
  const bracketYTop = -30 // tolerance bracket above the chart (inverted)

  function DurationBracket({
    x1,
    x2,
    y,
    label,
    color,
    opacity = 0.7,
    inverted = false,
  }: {
    x1: number
    x2: number
    y: number
    label: string
    color: string
    opacity?: number
    inverted?: boolean
  }) {
    const mid = (x1 + x2) / 2
    const tickH = 6
    // Normal:   ticks point upward from line, label sits below
    // Inverted: ticks point downward from line, label sits above
    const tickDir = inverted ? tickH : -tickH
    const labelY = inverted ? y - 8 : y + 18
    return (
      <g opacity={opacity}>
        <line x1={x1} y1={y} x2={x1} y2={y + tickDir} stroke={color} strokeWidth={1.5} />
        <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth={1.5} />
        <line x1={x2} y1={y} x2={x2} y2={y + tickDir} stroke={color} strokeWidth={1.5} />
        <text x={mid} y={labelY} textAnchor="middle" fontSize={16} fill={color} fontWeight="500">
          {label}
        </text>
      </g>
    )
  }

  return (
    <div className="space-y-3">
      {/* Timeline SVG */}
      <svg
        viewBox={`0 0 ${svgW} ${svgH}`}
        className="w-full"
        style={{ height: svgH }}
        overflow="visible"
      >
        <g transform={`translate(${marginLeft},${marginTop})`}>
          {/* Background chart area */}
          <rect x={0} y={0} width={chartW} height={chartH} className="fill-muted/30" rx={4} />

          {/* Y-axis gridlines at 0, 25, 50, 75, 100% */}
          {[0, 25, 50, 75, 100].map((soc) => (
            <g key={soc}>
              <line
                x1={0}
                y1={yScale(soc)}
                x2={chartW}
                y2={yScale(soc)}
                stroke="currentColor"
                strokeOpacity={0.1}
                strokeWidth={1}
              />
              <text
                x={-5}
                y={yScale(soc)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={14}
                fill="currentColor"
                opacity={0.4}
              >
                {soc}%
              </text>
            </g>
          ))}

          {/* Early unplug tolerance band */}
          {toleranceBand !== null && (
            <rect
              x={toleranceBand.x}
              y={0}
              width={toleranceBand.w}
              height={chartH}
              fill={UNPLUG_TIMING_COLOR.early}
              fillOpacity={0.08}
            />
          )}

          {/* SoC fill area */}
          <polygon points={fillPoints} fill={fillColor} fillOpacity={0.25} />
          {/* SoC fill top edge line */}
          <line
            x1={xPlug}
            y1={yPlugSoC}
            x2={xUnplug}
            y2={yUnplugSoC}
            stroke={fillColor}
            strokeWidth={2.5}
            strokeLinecap="round"
          />

          {/* Max feasible SoC dashed line */}
          {maxFeasibleSoC !== null && tDepart !== null && (
            <>
              <line
                x1={xPlug}
                y1={yScale(maxFeasibleSoC)}
                x2={xScale(tDepart)}
                y2={yScale(maxFeasibleSoC)}
                stroke={FEASIBILITY_COLOR[feasibility]}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                strokeOpacity={0.8}
              />
              <text
                x={xScale(tDepart) + 4}
                y={yScale(maxFeasibleSoC)}
                dominantBaseline="middle"
                fontSize={14}
                fill={FEASIBILITY_COLOR[feasibility]}
                opacity={0.9}
              >
                {Math.round(maxFeasibleSoC)}% max
              </text>
            </>
          )}

          {/* Scheduled departure vertical marker */}
          {tDepart !== null && (
            <line
              x1={xScale(tDepart)}
              y1={0}
              x2={xScale(tDepart)}
              y2={chartH}
              stroke={UNPLUG_TIMING_COLOR.ontime}
              strokeWidth={1.5}
              strokeDasharray="5 3"
              strokeOpacity={0.7}
            />
          )}

          {/* Plug-in vertical marker */}
          <line
            x1={xPlug}
            y1={0}
            x2={xPlug}
            y2={chartH}
            stroke="currentColor"
            strokeWidth={1}
            strokeOpacity={0.3}
          />

          {/* Unplug vertical marker */}
          <line
            x1={xUnplug}
            y1={0}
            x2={xUnplug}
            y2={chartH}
            stroke={timing === 'early' ? UNPLUG_TIMING_COLOR.early : 'currentColor'}
            strokeWidth={1.5}
            strokeOpacity={0.6}
          />

          {/* SoC value labels on the fill line endpoints */}
          <circle cx={xPlug} cy={yPlugSoC} r={4} fill={fillColor} />
          <text
            x={xPlug}
            y={labelsClose ? yPlugSoC - 12 : yPlugSoC - 8}
            textAnchor="middle"
            fontSize={16}
            fontWeight="600"
            fill={fillColor}
          >
            {s.pluggedSoC}%
          </text>

          <circle cx={xUnplug} cy={yUnplugSoC} r={4} fill={fillColor} />
          <text
            x={xUnplug}
            y={labelsClose ? yUnplugSoC + 18 : yUnplugSoC - 8}
            textAnchor="middle"
            fontSize={16}
            fontWeight="600"
            fill={fillColor}
          >
            {s.unpluggedSoC}%
          </text>

          {/* Target SoC threshold band + dotted line + left-side label */}
          {s.desiredSoC !== undefined && (
            <>
              {/* Shaded acceptance band: desiredSoC ± threshold */}
              <rect
                x={0}
                y={yScale(s.desiredSoC + threshold)}
                width={chartW}
                height={Math.max(
                  0,
                  yScale(s.desiredSoC - threshold) - yScale(s.desiredSoC + threshold)
                )}
                fill={fillColor}
                fillOpacity={0.07}
              />
              {/* Dotted target line */}
              <line
                x1={xPlug}
                y1={yScale(s.desiredSoC)}
                x2={xUnplug}
                y2={yScale(s.desiredSoC)}
                stroke={fillColor}
                strokeWidth={1}
                strokeDasharray="3 3"
                strokeOpacity={0.5}
              />
              {/* Label on the left y-axis side */}
              <text
                x={-5}
                y={yScale(s.desiredSoC)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={14}
                fill={fillColor}
                opacity={0.85}
              >
                {s.desiredSoC}%
              </text>
            </>
          )}

          {/* X-axis time labels */}
          {/* Plug-in */}
          <text
            x={xPlug}
            y={chartH + 18}
            textAnchor="middle"
            fontSize={16}
            fill="currentColor"
            opacity={0.6}
          >
            {fmtTime(tPlug)}
          </text>
          <text
            x={xPlug}
            y={chartH + 34}
            textAnchor="middle"
            fontSize={14}
            fill="currentColor"
            opacity={0.35}
          >
            {fmtDateShort(tPlug)}
          </text>

          {/* Scheduled departure */}
          {tDepart !== null && (
            <>
              <text
                x={xScale(tDepart)}
                y={chartH + 18}
                textAnchor="middle"
                fontSize={16}
                fill={UNPLUG_TIMING_COLOR.ontime}
                opacity={0.8}
              >
                {fmtTime(tDepart)}
              </text>
              <text
                x={xScale(tDepart)}
                y={chartH + 34}
                textAnchor="middle"
                fontSize={14}
                fill={UNPLUG_TIMING_COLOR.ontime}
                opacity={0.5}
              >
                depart
              </text>
            </>
          )}

          {/* Unplug time (suppress if very close to departure marker) */}
          {(tDepart === null || Math.abs(xUnplug - xScale(tDepart)) > 32) && (
            <>
              <text
                x={xUnplug}
                y={chartH + 18}
                textAnchor="middle"
                fontSize={16}
                fill={timing === 'early' ? UNPLUG_TIMING_COLOR.early : 'currentColor'}
                opacity={0.8}
              >
                {fmtTime(tUnplug)}
              </text>
              <text
                x={xUnplug}
                y={chartH + 34}
                textAnchor="middle"
                fontSize={14}
                fill={timing === 'early' ? UNPLUG_TIMING_COLOR.early : 'currentColor'}
                opacity={timing === 'early' ? 0.7 : 0.35}
              >
                {timing === 'early' ? 'early unplug' : fmtDateShort(tUnplug)}
              </text>
            </>
          )}

          {/* Actual duration bracket */}
          <DurationBracket
            x1={xPlug}
            x2={xUnplug}
            y={bracketY1}
            label={`actual ${fmtDuration(actualDurationMs)}`}
            color="currentColor"
            opacity={0.45}
          />

          {/* Planned duration bracket */}
          {plannedDurationMs !== null && plannedDurationMs > 0 && (
            <DurationBracket
              x1={xPlug}
              x2={xScale(tDepart!)}
              y={bracketY2}
              label={`planned ${fmtDuration(plannedDurationMs)}`}
              color={UNPLUG_TIMING_COLOR.ontime}
              opacity={0.6}
            />
          )}

          {/* Early unplug tolerance bracket — above the chart, inverted */}
          {tDepart !== null && toleranceBand !== null && (
            <DurationBracket
              x1={toleranceBand.x}
              x2={xScale(tDepart)}
              y={bracketYTop}
              label={`${earlyUnplugToleranceMin}m tolerance`}
              color={UNPLUG_TIMING_COLOR.early}
              opacity={0.6}
              inverted
            />
          )}

          {/* Battery icon (right margin) */}
          <g transform={`translate(${batX},${batY})`}>
            {/* Battery body */}
            <rect
              x={0}
              y={4}
              width={batW}
              height={batH - 4}
              rx={3}
              ry={3}
              fill="none"
              stroke={batteryColor}
              strokeWidth={1.5}
              strokeOpacity={0.6}
            />
            {/* Battery terminal nub */}
            <rect
              x={batW * 0.3}
              y={0}
              width={batW * 0.4}
              height={5}
              rx={1.5}
              ry={1.5}
              fill={batteryColor}
              fillOpacity={0.5}
            />
            {/* Battery fill */}
            <rect
              x={2}
              y={4 + (batH - 4) * (1 - batFill) + 2}
              width={batW - 4}
              height={Math.max(0, (batH - 4) * batFill - 2)}
              rx={2}
              ry={2}
              fill={batteryColor}
              fillOpacity={0.55}
            />
            {/* kWh annotation below */}
            <text
              x={batW / 2}
              y={batH + 20}
              textAnchor="middle"
              fontSize={16}
              fontWeight="600"
              fill={batteryColor}
              fillOpacity={0.8}
            >
              {batteryKWh.toFixed(1)} kWh
            </text>
          </g>
        </g>
      </svg>

      {/* Metadata row — power only; durations and target are annotated on the chart */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm px-1">
        <div>
          <p className="text-muted-foreground mb-1 flex items-center gap-1">
            Max reported power
            <InfoTip
              content={
                <p>
                  The peak charging power recorded for this session, as reported by the charger or
                  vehicle. May be lower than the charger's rated capacity if the vehicle limited the
                  charge rate.
                </p>
              }
            />
          </p>
          <p className="font-medium text-base">{formatPower(s.maxChargePower)}</p>
        </div>
        <div>
          <p className="text-muted-foreground mb-1 flex items-center gap-1">
            Observed avg power
            <InfoTip
              content={
                <p>
                  Average charging power calculated from the actual energy delivered: (end SoC −
                  start SoC) × battery capacity ÷ session duration. This reflects what was actually
                  observed rather than what was reported by the charger.
                </p>
              }
            />
          </p>
          <p className="font-medium text-base">{avgPower !== null ? formatPower(avgPower) : '—'}</p>
        </div>
      </div>
    </div>
  )
}

export default function SoCJourneyScatter({
  poolID,
  timeRange,
  threshold: thresholdProp,
  onThresholdChange,
  earlyUnplugToleranceMin: earlyUnplugToleranceMinProp,
  onEarlyUnplugToleranceMinChange,
  hiddenOutcomes: hiddenOutcomesProp,
  onHiddenOutcomesChange,
  hiddenDurations: hiddenDurationsProp,
  onHiddenDurationsChange,
  hiddenTimings: hiddenTimingsProp,
  onHiddenTimingsChange,
  hiddenFeasibilities: hiddenFeasibilitiesProp,
  onHiddenFeasibilitiesChange,
  selectedSessionId,
  onSelectedSessionIdChange,
}: SoCJourneyScatterProps) {
  const [thresholdInternal, setThresholdInternal] = useState(2)
  const [earlyUnplugToleranceMinInternal, setEarlyUnplugToleranceMinInternal] = useState(30)

  const isControlledThreshold = thresholdProp !== undefined
  const threshold = isControlledThreshold ? thresholdProp : thresholdInternal
  const setThreshold = (v: number) => {
    if (isControlledThreshold) onThresholdChange?.(v)
    else setThresholdInternal(v)
  }

  const isControlledEarlyUnplug = earlyUnplugToleranceMinProp !== undefined
  const earlyUnplugToleranceMin = isControlledEarlyUnplug
    ? earlyUnplugToleranceMinProp
    : earlyUnplugToleranceMinInternal
  const setEarlyUnplugToleranceMin = (v: number) => {
    if (isControlledEarlyUnplug) onEarlyUnplugToleranceMinChange?.(v)
    else setEarlyUnplugToleranceMinInternal(v)
  }

  // Outcome pills — controlled or internal
  const [hiddenOutcomesInternal, setHiddenOutcomesInternal] = useState<Set<Outcome>>(new Set())
  const isControlledOutcomes = hiddenOutcomesProp !== undefined
  const hiddenOutcomes: Set<Outcome> = isControlledOutcomes
    ? (hiddenOutcomesProp as Set<Outcome>)
    : hiddenOutcomesInternal

  // Duration pills — controlled or internal
  const [hiddenDurationsInternal, setHiddenDurationsInternal] = useState<Set<DurationBucket>>(
    new Set()
  )
  const isControlledDurations = hiddenDurationsProp !== undefined
  const hiddenDurations: Set<DurationBucket> = isControlledDurations
    ? (hiddenDurationsProp as Set<DurationBucket>)
    : hiddenDurationsInternal

  // Unplug-timing pills — controlled or internal (early hidden by default)
  const [hiddenTimingsInternal, setHiddenTimingsInternal] = useState<Set<UnplugTiming>>(
    new Set(['early'])
  )
  const isControlledTimings = hiddenTimingsProp !== undefined
  const hiddenTimings: Set<UnplugTiming> = isControlledTimings
    ? (hiddenTimingsProp as Set<UnplugTiming>)
    : hiddenTimingsInternal

  // Feasibility pills — controlled or internal (infeasible + unknown hidden by default)
  const [hiddenFeasibilitiesInternal, setHiddenFeasibilitiesInternal] = useState<Set<Feasibility>>(
    new Set(['infeasible', 'unknown'])
  )
  const isControlledFeasibilities = hiddenFeasibilitiesProp !== undefined
  const hiddenFeasibilities: Set<Feasibility> = isControlledFeasibilities
    ? (hiddenFeasibilitiesProp as Set<Feasibility>)
    : hiddenFeasibilitiesInternal

  const [tooltip, setTooltip] = useState<TooltipData | null>(null)
  // Internal selected-session state (used when not in controlled mode)
  const [selectedSessionInternal, setSelectedSessionInternal] = useState<{
    session: ChargingSession
    outcome: Outcome
  } | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const { data: sessions = [], isFetching } = useChargingSessions(poolID, timeRange)

  // Resolve selected session from controlled ID or internal state
  const isControlledSession = selectedSessionId !== undefined
  const selectedSession: { session: ChargingSession; outcome: Outcome } | null = (() => {
    if (isControlledSession) {
      if (!selectedSessionId) return null
      const found = sessions.find((s: ChargingSession) => s.id === selectedSessionId)
      if (!found) return null
      return { session: found, outcome: getOutcome(found, threshold) }
    }
    return selectedSessionInternal
  })()

  const setSelectedSession = (val: { session: ChargingSession; outcome: Outcome } | null) => {
    if (isControlledSession) {
      onSelectedSessionIdChange?.(val ? val.session.id : null)
    } else {
      setSelectedSessionInternal(val)
    }
  }

  const toggleOutcome = (outcome: Outcome) => {
    const next = new Set(hiddenOutcomes)
    if (next.has(outcome)) next.delete(outcome)
    else next.add(outcome)
    if (isControlledOutcomes) onHiddenOutcomesChange?.(next as Set<string>)
    else setHiddenOutcomesInternal(next)
  }

  const toggleDuration = (bucket: DurationBucket) => {
    const next = new Set(hiddenDurations)
    if (next.has(bucket)) next.delete(bucket)
    else next.add(bucket)
    if (isControlledDurations) onHiddenDurationsChange?.(next as Set<string>)
    else setHiddenDurationsInternal(next)
  }

  const toggleTiming = (timing: UnplugTiming) => {
    const next = new Set(hiddenTimings)
    if (next.has(timing)) next.delete(timing)
    else next.add(timing)
    if (isControlledTimings) onHiddenTimingsChange?.(next as Set<string>)
    else setHiddenTimingsInternal(next)
  }

  const toggleFeasibility = (f: Feasibility) => {
    const next = new Set(hiddenFeasibilities)
    if (next.has(f)) next.delete(f)
    else next.add(f)
    if (isControlledFeasibilities) onHiddenFeasibilitiesChange?.(next as Set<string>)
    else setHiddenFeasibilitiesInternal(next)
  }

  // Derive plot points — exclude forced-mode sessions
  const points = useMemo<PlotPoint[]>(
    () =>
      sessions
        .filter((s: ChargingSession) => s.chargeMode !== 'FORCED_EV' && s.desiredSoC !== undefined)
        .map((s: ChargingSession) => ({
          session: s,
          outcome: getOutcome(s, threshold),
          durationBucket: getDurationBucket(s),
          unplugTiming: getUnplugTiming(s, earlyUnplugToleranceMin),
          feasibility: getFeasibility(s),
          durationHours:
            (new Date(s.unpluggedAt).getTime() - new Date(s.pluggedAt).getTime()) /
            (1000 * 60 * 60),
          socGain: s.unpluggedSoC - s.pluggedSoC,
          targetSoC: s.desiredSoC,
          pointColor: targetFulfilmentColor(s.unpluggedSoC, s.desiredSoC, Math.max(1, threshold)),
        })),
    [sessions, threshold, earlyUnplugToleranceMin]
  )

  const visiblePoints = useMemo(
    () =>
      points.filter(
        (p) =>
          !hiddenOutcomes.has(p.outcome) &&
          !hiddenDurations.has(p.durationBucket) &&
          (p.unplugTiming === null || !hiddenTimings.has(p.unplugTiming)) &&
          !hiddenFeasibilities.has(p.feasibility)
      ),
    [points, hiddenOutcomes, hiddenDurations, hiddenTimings, hiddenFeasibilities]
  )

  // Count by outcome
  const outcomeCounts = useMemo(() => {
    const c: Record<Outcome, number> = { success: 0, failed: 0, no_charge: 0 }
    points.forEach((p) => c[p.outcome]++)
    return c
  }, [points])

  // Count by duration bucket
  const durationCounts = useMemo(() => {
    const c: Record<DurationBucket, number> = { tiny: 0, short: 0, medium: 0, long: 0, verylong: 0 }
    points.forEach((p) => c[p.durationBucket]++)
    return c
  }, [points])

  // Count by unplug timing (null entries are not counted against any bucket)
  const timingCounts = useMemo(() => {
    const c: Record<UnplugTiming, number> = { early: 0, ontime: 0 }
    points.forEach((p) => {
      if (p.unplugTiming !== null) c[p.unplugTiming]++
    })
    return c
  }, [points])

  // Count by feasibility (null entries are not counted against any bucket)
  const feasibilityCounts = useMemo(() => {
    const c: Record<Feasibility, number> = { feasible: 0, infeasible: 0, unknown: 0 }
    points.forEach((p) => {
      if (p.feasibility !== null) c[p.feasibility]++
    })
    return c
  }, [points])

  // Chart dimensions — responsive via container size
  const [chartWidth, setChartWidth] = useState(480)
  const [chartHeight, setChartHeight] = useState(320)
  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect
      setChartWidth(width)
      if (height > 0) setChartHeight(height)
    })
    ro.observe(node)
    // seed immediately so first render isn't 320px
    const rect = node.getBoundingClientRect()
    setChartWidth(rect.width)
    if (rect.height > 0) setChartHeight(rect.height)
    return () => ro.disconnect()
  }, [])

  const innerW = chartWidth - MARGIN.left - MARGIN.right
  const innerH = Math.max(100, chartHeight - MARGIN.top - MARGIN.bottom)

  // X axis: diverging power-log scale centred on the mean session duration.
  //
  // Transform: t(h) = sign(log₂(h/mean)) * |log₂(h/mean)|^POWER
  //
  // POWER < 1 (cube root) stretches the centre (near-average sessions) and
  // compresses the extremes, pulling apart the dense cluster around the mean.
  const LOG_TICK_CANDIDATES = [0.25, 0.5, 1, 2, 5, 10, 20, 50, 100] // hours
  const LOG_POWER = 2 / 3

  const xTransform = useCallback((hours: number, median: number) => {
    const r = Math.log2(Math.max(hours, 0.001) / median)
    return Math.sign(r) * Math.pow(Math.abs(r), LOG_POWER)
  }, [])

  const [medianDurationHours, tLeft, tRight] = useMemo(() => {
    if (visiblePoints.length === 0) {
      const t = Math.pow(Math.log2(6), LOG_POWER)
      return [4, t, t]
    }
    const sorted = [...visiblePoints].sort((a, b) => a.durationHours - b.durationHours)
    const mid = Math.floor(sorted.length / 2)
    const median =
      sorted.length % 2 === 1
        ? sorted[mid].durationHours
        : (sorted[mid - 1].durationHours + sorted[mid].durationHours) / 2
    const safeMedian = Math.max(median, 0.01)
    // Left and right extents computed independently
    const leftRange = Math.max(
      ...visiblePoints.map((p) => Math.max(0, -xTransform(p.durationHours, safeMedian))),
      0.1
    )
    const rightRange = Math.max(
      ...visiblePoints.map((p) => Math.max(0, xTransform(p.durationHours, safeMedian))),
      0.1
    )
    return [safeMedian, leftRange, rightRange]
  }, [visiblePoints, xTransform])

  // Ticks: candidate hours that fall within the visible transformed range
  const xTicks = useMemo(
    () =>
      LOG_TICK_CANDIDATES.filter((h) => {
        const t = xTransform(h, medianDurationHours)
        return t >= -tLeft * 1.01 && t <= tRight * 1.01
      }),
    [medianDurationHours, tLeft, tRight, xTransform]
  )

  // Map duration (hours) → pixel x using the power-log transform.
  // t < 0 (shorter than median) maps into [0, medianX]; t > 0 into [medianX, innerW].
  const medianX = useMemo(() => (tLeft / (tLeft + tRight)) * innerW, [tLeft, tRight, innerW])
  const scaleX = useCallback(
    (hours: number) => {
      const t = xTransform(hours, medianDurationHours)
      if (t <= 0) return (1 - Math.abs(t) / tLeft) * medianX
      return medianX + (t / tRight) * (innerW - medianX)
    },
    [innerW, medianDurationHours, tLeft, tRight, medianX, xTransform]
  )
  const scaleY = useCallback((gain: number) => innerH - (gain / 100) * innerH, [innerH])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || visiblePoints.length === 0) return
      const rect = svgRef.current.getBoundingClientRect()
      const mx = e.clientX - rect.left - MARGIN.left
      const my = e.clientY - rect.top - MARGIN.top
      let nearest: PlotPoint | null = null
      let minDist = 20
      for (const p of visiblePoints) {
        const px = scaleX(p.durationHours)
        const py = scaleY(p.socGain)
        const d = Math.sqrt((px - mx) ** 2 + (py - my) ** 2)
        if (d < minDist) {
          minDist = d
          nearest = p
        }
      }
      if (nearest) {
        setTooltip({
          x: scaleX(nearest.durationHours) + MARGIN.left,
          y: scaleY(nearest.socGain) + MARGIN.top,
          session: nearest.session,
          outcome: nearest.outcome,
          pointColor: nearest.pointColor,
        })
      } else {
        setTooltip(null)
      }
    },
    [visiblePoints, scaleX, scaleY]
  )

  const handleClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || visiblePoints.length === 0) return
      const rect = svgRef.current.getBoundingClientRect()
      const mx = e.clientX - rect.left - MARGIN.left
      const my = e.clientY - rect.top - MARGIN.top
      let nearest: PlotPoint | null = null
      let minDist = 20
      for (const p of visiblePoints) {
        const px = scaleX(p.durationHours)
        const py = scaleY(p.socGain)
        const d = Math.sqrt((px - mx) ** 2 + (py - my) ** 2)
        if (d < minDist) {
          minDist = d
          nearest = p
        }
      }
      if (nearest) {
        setSelectedSession({ session: nearest.session, outcome: nearest.outcome })
      }
    },
    [visiblePoints, scaleX, scaleY]
  )

  const gridLines = [0, 25, 50, 75, 100]

  const successRate = useMemo(() => {
    if (visiblePoints.length === 0) return null
    const n = visiblePoints.filter((p) => p.outcome === 'success').length
    return Math.round((n / visiblePoints.length) * 100)
  }, [visiblePoints])

  return (
    <>
      <Card className="h-full flex flex-col">
        <CardHeader className="shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ScatterChart className="h-5 w-5" />
                Session Success Analysis
                <InfoTip
                  content={
                    <>
                      <p className="font-medium mb-1">Session Success Analysis</p>
                      <p>
                        Each dot is one completed scheduled charging session. Its horizontal
                        position is the session <em>duration</em> on a log scale centred on the
                        median — shorter sessions sit left, longer ones right. Its vertical position
                        is the <em>SoC gain</em> (end SoC minus start SoC). Forced-mode sessions are
                        excluded.
                      </p>
                      <p className="mt-1">
                        Dot color shows how close the session came to its target SoC: green means
                        the target was met or exceeded, yellow means slightly short, red means far
                        short. Use the Outcome dropdown to adjust what counts as "met target".
                      </p>
                      <p className="mt-1 text-muted-foreground">
                        Use the filter dropdowns to narrow by outcome, duration, unplug timing, or
                        feasibility. The Outcome and Timing dropdowns contain sliders to tune the
                        success threshold and early-unplug tolerance.
                      </p>
                    </>
                  }
                />
              </CardTitle>
              <CardDescription>
                Session duration vs SoC gain — color shows how close each session came to its target
              </CardDescription>
            </div>
            <div className="text-right shrink-0 whitespace-nowrap">
              {/* Always render all three lines to keep header height stable */}
              <p
                className={`text-2xl font-bold tabular-nums leading-none transition-opacity ${isFetching ? 'opacity-40' : ''}`}
                style={{
                  color:
                    successRate !== null
                      ? successRate >= 80
                        ? OUTCOME_COLOR.success
                        : successRate >= 50
                          ? '#f59e0b'
                          : OUTCOME_COLOR.failed
                      : 'transparent',
                }}
              >
                {successRate !== null ? (
                  `${successRate}%`
                ) : isFetching ? (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground inline-block" />
                ) : (
                  '\u00a0'
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-1">success rate</p>
              <p className="text-xs text-muted-foreground">
                {successRate !== null ? (
                  <>
                    {visiblePoints.filter((p) => p.outcome === 'success').length} /{' '}
                    {visiblePoints.length} sessions
                  </>
                ) : (
                  '\u00a0'
                )}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 min-h-0 flex flex-col p-6 pt-0">
          <div className="flex flex-col flex-1 min-h-0 gap-4">
            {/* Category filter dropdowns */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Outcome */}
              <FilterDropdown
                label="Outcome"
                hiddenCount={hiddenOutcomes.size}
                items={(Object.keys(OUTCOME_LABEL) as Outcome[]).map((k) => ({
                  key: k,
                  label: OUTCOME_LABEL[k],
                  color: OUTCOME_COLOR[k],
                  count: outcomeCounts[k],
                  checked: !hiddenOutcomes.has(k),
                  onToggle: () => toggleOutcome(k),
                }))}
                tooltip={
                  <ul className="space-y-0.5">
                    <li>
                      <strong style={{ color: OUTCOME_COLOR.success }}>Met target</strong> — ended
                      at or above the desired SoC minus the threshold.
                    </li>
                    <li>
                      <strong style={{ color: OUTCOME_COLOR.failed }}>Missed target</strong> — ended
                      below the threshold.
                    </li>
                    <li>
                      <strong style={{ color: OUTCOME_COLOR.no_charge }}>No charge</strong> — SoC
                      was unchanged; the session failed to deliver any energy.
                    </li>
                    <li className="mt-1 text-muted-foreground">
                      <strong>Threshold slider</strong> — how many percentage points below the
                      desired SoC still counts as Met target (0 = exact match required, 5 = up to 5
                      pp short is accepted).
                    </li>
                  </ul>
                }
                footer={
                  <div className="space-y-1.5 w-36">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Threshold</span>
                      <span className="text-xs font-medium tabular-nums">
                        {threshold === 0 ? '±0%' : `–${threshold}%`}
                      </span>
                    </div>
                    <Slider
                      min={0}
                      max={5}
                      step={1}
                      value={[threshold]}
                      onValueChange={([v]: number[]) => setThreshold(v)}
                      className="w-full"
                    />
                  </div>
                }
              />

              {/* Duration */}
              <FilterDropdown
                label="Duration"
                hiddenCount={hiddenDurations.size}
                items={(Object.keys(DURATION_LABEL) as DurationBucket[]).map((k) => ({
                  key: k,
                  label: DURATION_LABEL[k],
                  color: DURATION_COLOR[k],
                  count: durationCounts[k],
                  checked: !hiddenDurations.has(k),
                  onToggle: () => toggleDuration(k),
                }))}
                tooltip={
                  <ul className="space-y-0.5">
                    <li>
                      <strong style={{ color: DURATION_COLOR.tiny }}>{'< 1 h'}</strong> — very short
                      session.
                    </li>
                    <li>
                      <strong style={{ color: DURATION_COLOR.short }}>1 – 6 h</strong> — short
                      session.
                    </li>
                    <li>
                      <strong style={{ color: DURATION_COLOR.medium }}>6 – 12 h</strong> — medium
                      session.
                    </li>
                    <li>
                      <strong style={{ color: DURATION_COLOR.long }}>12 – 24 h</strong> — long
                      session.
                    </li>
                    <li>
                      <strong style={{ color: DURATION_COLOR.verylong }}>{'> 24 h'}</strong> — very
                      long session.
                    </li>
                  </ul>
                }
              />

              {/* Timing */}
              <FilterDropdown
                label="Timing"
                hiddenCount={hiddenTimings.size}
                items={(Object.keys(UNPLUG_TIMING_LABEL) as UnplugTiming[]).map((k) => ({
                  key: k,
                  label: UNPLUG_TIMING_LABEL[k],
                  color: UNPLUG_TIMING_COLOR[k],
                  count: timingCounts[k],
                  checked: !hiddenTimings.has(k),
                  onToggle: () => toggleTiming(k),
                }))}
                tooltip={
                  <ul className="space-y-0.5">
                    <li>
                      <strong style={{ color: UNPLUG_TIMING_COLOR.early }}>Early unplug</strong> —
                      car was unplugged before its scheduled departure time.
                    </li>
                    <li>
                      <strong style={{ color: UNPLUG_TIMING_COLOR.ontime }}>On time</strong> — car
                      was unplugged at or after its scheduled departure time.
                    </li>
                    <li className="mt-1 text-muted-foreground">
                      <strong>Tolerance slider</strong> — grace period in minutes; an unplug must
                      occur at least this far before the scheduled departure to be classified as
                      Early (0 = any early unplug counts).
                    </li>
                  </ul>
                }
                footer={
                  <div className="space-y-1.5 w-36">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Tolerance</span>
                      <span className="text-xs font-medium tabular-nums">
                        {earlyUnplugToleranceMin === 0 ? 'none' : `${earlyUnplugToleranceMin} min`}
                      </span>
                    </div>
                    <Slider
                      min={0}
                      max={60}
                      step={5}
                      value={[earlyUnplugToleranceMin]}
                      onValueChange={([v]: number[]) => setEarlyUnplugToleranceMin(v)}
                      className="w-full"
                    />
                  </div>
                }
              />

              {/* Feasibility */}
              <FilterDropdown
                label="Feasibility"
                hiddenCount={hiddenFeasibilities.size}
                items={(Object.keys(FEASIBILITY_LABEL) as Feasibility[]).map((k) => ({
                  key: k,
                  label: FEASIBILITY_LABEL[k],
                  color: FEASIBILITY_COLOR[k],
                  count: feasibilityCounts[k],
                  checked: !hiddenFeasibilities.has(k),
                  onToggle: () => toggleFeasibility(k),
                }))}
                tooltip={
                  <ul className="space-y-0.5">
                    <li>
                      <strong style={{ color: FEASIBILITY_COLOR.feasible }}>Feasible</strong> — the
                      target SoC could have been reached within the planned session duration at max
                      charge power.
                    </li>
                    <li>
                      <strong style={{ color: FEASIBILITY_COLOR.infeasible }}>Infeasible</strong> —
                      even charging at max power for the full planned duration would not have been
                      enough to reach the target SoC.
                    </li>
                    <li>
                      <strong style={{ color: FEASIBILITY_COLOR.unknown }}>Unknown</strong> —
                      feasibility could not be determined (no schedule, no target, or average power
                      not recorded).
                    </li>
                  </ul>
                }
              />
            </div>

            {/* SVG scatter plot */}
            <div ref={measuredRef} className="w-full flex-1 min-h-0 relative">
              {!isFetching && sessions.length === 0 ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
                  <p>No charging sessions found</p>
                  <p className="text-sm mt-2">No EV charging activity in the selected time range</p>
                </div>
              ) : (
                <>
                  <svg
                    ref={svgRef}
                    width={chartWidth}
                    height={innerH + MARGIN.top + MARGIN.bottom}
                    style={{
                      width: '100%',
                      height: '100%',
                      opacity: isFetching ? 0.25 : 1,
                      transition: 'opacity 0.15s',
                    }}
                    className="overflow-visible cursor-pointer"
                    onMouseMove={handleMouseMove}
                    onMouseLeave={() => setTooltip(null)}
                    onClick={handleClick}
                  >
                    <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
                      {/* X grid lines at each tick value */}
                      {xTicks.map((h) => {
                        const label = h < 1 ? `${Math.round(h * 60)}m` : `${h}h`
                        return (
                          <g key={`x-${h}`}>
                            <line
                              x1={scaleX(h)}
                              x2={scaleX(h)}
                              y1={0}
                              y2={innerH}
                              stroke="currentColor"
                              strokeOpacity={0.08}
                              strokeWidth={1}
                            />
                            <text
                              x={scaleX(h)}
                              y={innerH + 16}
                              textAnchor="middle"
                              fontSize={11}
                              fill="currentColor"
                              opacity={0.5}
                            >
                              {label}
                            </text>
                          </g>
                        )
                      })}

                      {/* Median-duration reference line + label above the plot */}
                      <line
                        x1={medianX}
                        x2={medianX}
                        y1={0}
                        y2={innerH}
                        stroke="currentColor"
                        strokeOpacity={0.45}
                        strokeWidth={1.5}
                        strokeDasharray="4,3"
                      />
                      <text
                        x={medianX}
                        y={-8}
                        fontSize={10}
                        fill="currentColor"
                        opacity={0.6}
                        textAnchor="middle"
                        fontWeight="500"
                      >
                        {medianDurationHours < 1
                          ? `median ${Math.round(medianDurationHours * 60)}m`
                          : `median ${medianDurationHours.toFixed(1)}h`}
                      </text>

                      {/* Y grid lines — 0, 25, 50, 75, 100 */}
                      {gridLines.map((v) => (
                        <g key={`y-${v}`}>
                          <line
                            x1={0}
                            x2={innerW}
                            y1={scaleY(v)}
                            y2={scaleY(v)}
                            stroke="currentColor"
                            strokeOpacity={0.08}
                            strokeWidth={1}
                          />
                          <text
                            x={-10}
                            y={scaleY(v) + 4}
                            textAnchor="end"
                            fontSize={11}
                            fill="currentColor"
                            opacity={0.5}
                          >
                            {v}%
                          </text>
                        </g>
                      ))}

                      {/* Axis labels */}
                      <text
                        x={innerW / 2}
                        y={innerH + 40}
                        textAnchor="middle"
                        fontSize={12}
                        fill="currentColor"
                        opacity={0.6}
                      >
                        Duration — log scale, centred on median (h)
                      </text>
                      <text
                        x={-innerH / 2}
                        y={-36}
                        transform="rotate(-90)"
                        textAnchor="middle"
                        fontSize={12}
                        fill="currentColor"
                        opacity={0.6}
                      >
                        SoC Gain (%)
                      </text>

                      {/* Chart border */}
                      <rect
                        x={0}
                        y={0}
                        width={innerW}
                        height={innerH}
                        fill="none"
                        stroke="currentColor"
                        strokeOpacity={0.1}
                        strokeWidth={1}
                      />

                      {/* Data points — radius and opacity scale down with session count */}
                      {(() => {
                        const n = visiblePoints.length
                        const r = n > 500 ? 2.5 : n > 100 ? 3.5 : 5
                        const opacity = n > 1000 ? 0.35 : n > 200 ? 0.5 : 0.7
                        return visiblePoints.map((p, i) => (
                          <circle
                            key={i}
                            cx={scaleX(p.durationHours)}
                            cy={scaleY(p.socGain)}
                            r={r}
                            fill={p.pointColor}
                            fillOpacity={opacity}
                            stroke="none"
                          />
                        ))
                      })()}

                      {/* Tooltip highlight ring */}
                      {tooltip && (
                        <circle
                          cx={tooltip.x - MARGIN.left}
                          cy={tooltip.y - MARGIN.top}
                          r={8}
                          fill="none"
                          stroke={tooltip.pointColor}
                          strokeWidth={2}
                        />
                      )}
                    </g>
                  </svg>

                  {/* Tooltip — rendered as HTML so it inherits correct theme colors */}
                  {tooltip &&
                    (() => {
                      const s = tooltip.session
                      const boxW = 196
                      const boxH = 76
                      const gap = 12
                      const left =
                        tooltip.x + gap + boxW + 8 <= chartWidth
                          ? tooltip.x + gap // fits to the right
                          : tooltip.x - boxW - gap // flip to the left
                      const top = Math.max(tooltip.y - boxH / 2, 4)
                      return (
                        <div
                          className="absolute pointer-events-none rounded-md border bg-popover text-popover-foreground shadow-md px-3 py-2 text-xs"
                          style={{ left, top, width: boxW }}
                        >
                          <p className="font-medium">{formatDate(s.pluggedAt)}</p>
                          <p className="text-muted-foreground mt-0.5">
                            {(() => {
                              const h = Math.floor(
                                (new Date(s.unpluggedAt).getTime() -
                                  new Date(s.pluggedAt).getTime()) /
                                  (1000 * 60 * 60)
                              )
                              const m = Math.floor(
                                ((new Date(s.unpluggedAt).getTime() -
                                  new Date(s.pluggedAt).getTime()) %
                                  (1000 * 60 * 60)) /
                                  (1000 * 60)
                              )
                              return h > 0 ? `${h}h ${m}m` : `${m}m`
                            })()}
                          </p>
                          <p className="text-muted-foreground">
                            SoC: {s.pluggedSoC}% → {s.unpluggedSoC}% (+
                            {s.unpluggedSoC - s.pluggedSoC}%)
                          </p>
                          <p className="text-muted-foreground">
                            {s.desiredSoC !== undefined
                              ? `Target: ${s.desiredSoC}%`
                              : 'Forced mode'}
                          </p>
                          <p className="font-semibold mt-0.5" style={{ color: tooltip.pointColor }}>
                            {OUTCOME_LABEL[tooltip.outcome]}
                          </p>
                        </div>
                      )
                    })()}
                </>
              )}
              {isFetching && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Session detail dialog */}
      <Dialog
        open={selectedSession !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedSession(null)
        }}
      >
        <DialogContent className="max-w-[95vw] w-[1100px]">
          {selectedSession && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-3 rounded-full"
                    style={{ backgroundColor: OUTCOME_COLOR[selectedSession.outcome] }}
                  />
                  Charging Session
                  <InfoTip
                    side="right"
                    content={
                      <div className="space-y-2 text-xs leading-relaxed">
                        <p className="font-semibold text-sm">How to read this chart</p>
                        <p>
                          The timeline runs left to right from plug-in to unplug (or scheduled
                          departure, whichever is later). The filled area shows the battery State of
                          Charge rising over time, interpolated linearly between the measured start
                          and end values.
                        </p>
                        <div className="space-y-1">
                          <p className="font-medium">Subtitle pills</p>
                          <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                            <li>
                              <span
                                className="font-medium"
                                style={{ color: OUTCOME_COLOR.success }}
                              >
                                Met target
                              </span>{' '}
                              /{' '}
                              <span className="font-medium" style={{ color: OUTCOME_COLOR.failed }}>
                                Missed target
                              </span>{' '}
                              — whether the final SoC reached the user's requested SoC within the
                              acceptance threshold.
                            </li>
                            <li>
                              <span
                                className="font-medium"
                                style={{ color: FEASIBILITY_COLOR.feasible }}
                              >
                                Feasible
                              </span>{' '}
                              /{' '}
                              <span
                                className="font-medium"
                                style={{ color: FEASIBILITY_COLOR.infeasible }}
                              >
                                Infeasible
                              </span>{' '}
                              — whether the target was physically reachable within the planned
                              session duration at max charge power.
                            </li>
                            <li>
                              <span className="font-medium">Forced</span> /{' '}
                              <span className="font-medium">Scheduled</span> — charge mode. Forced
                              means no departure time was set; Scheduled means the system optimised
                              around a requested departure.
                            </li>
                          </ul>
                        </div>
                        <div className="space-y-1">
                          <p className="font-medium">Chart elements</p>
                          <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                            <li>
                              <span className="font-medium">Dotted horizontal line</span> — target
                              SoC requested by the user. The{' '}
                              <span className="font-medium">shaded band</span> around it is the
                              acceptance window (±threshold%).
                            </li>
                            <li>
                              <span
                                className="font-medium"
                                style={{ color: FEASIBILITY_COLOR.feasible }}
                              >
                                Dashed line (feasibility)
                              </span>{' '}
                              — maximum SoC reachable by the scheduled departure time if the charger
                              ran at full power the entire session.
                            </li>
                            <li>
                              <span
                                className="font-medium"
                                style={{ color: UNPLUG_TIMING_COLOR.ontime }}
                              >
                                Dashed vertical line
                              </span>{' '}
                              — scheduled departure time.
                            </li>
                            <li>
                              <span
                                className="font-medium"
                                style={{ color: UNPLUG_TIMING_COLOR.early }}
                              >
                                Pink shaded band
                              </span>{' '}
                              — early-unplug tolerance window before the scheduled departure.
                              Unplugging inside this window is still counted as on-time.
                            </li>
                          </ul>
                        </div>
                        <div className="space-y-1">
                          <p className="font-medium">Duration brackets (below chart)</p>
                          <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                            <li>
                              <span className="font-medium">Actual</span> — time from plug-in to
                              unplug.
                            </li>
                            <li>
                              <span
                                className="font-medium"
                                style={{ color: UNPLUG_TIMING_COLOR.ontime }}
                              >
                                Planned
                              </span>{' '}
                              — time from plug-in to the scheduled departure.
                            </li>
                            <li>
                              <span
                                className="font-medium"
                                style={{ color: UNPLUG_TIMING_COLOR.early }}
                              >
                                Tolerance (above chart)
                              </span>{' '}
                              — the early-unplug tolerance window expressed as a duration.
                            </li>
                          </ul>
                        </div>
                      </div>
                    }
                  />
                </DialogTitle>
                <DialogDescription className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span style={{ color: OUTCOME_COLOR[selectedSession.outcome] }}>
                    {OUTCOME_LABEL[selectedSession.outcome]}
                  </span>
                  <span className="opacity-30">·</span>
                  <span
                    style={{
                      color: FEASIBILITY_COLOR[getFeasibility(selectedSession.session)],
                    }}
                  >
                    {FEASIBILITY_LABEL[getFeasibility(selectedSession.session)]}
                  </span>
                  <span className="opacity-30">·</span>
                  <span>
                    {selectedSession.session.chargeMode === 'FORCED_EV' ? 'Forced' : 'Scheduled'}
                  </span>
                  <span className="opacity-30">·</span>
                  <span>{formatDate(selectedSession.session.pluggedAt)}</span>
                </DialogDescription>
              </DialogHeader>
              <SessionDetailVisual
                session={selectedSession.session}
                outcome={selectedSession.outcome}
                earlyUnplugToleranceMin={earlyUnplugToleranceMin}
                threshold={threshold}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
